import { ContextlessQueryObserverOptions, useSchemaQuery } from '@frontend/react-query-helpers';
import { SchemaIO } from '@frontend/schema';
import { trackScheduleAPIVersionChange } from '../helpers';
import { SchemaSchedulerV3Service, serviceName } from '../service';
import { ServiceQueries, ServiceStandardQueries, StandardQueryEndpointName } from '../types';

const DEFAULT_OPTIONS: ContextlessQueryObserverOptions = {
  refetchOnWindowFocus: false,
};

export type UseSchedulerV3QueryArgs<
  EndpointName extends StandardQueryEndpointName,
  E = unknown,
  D = SchemaIO<ServiceQueries[EndpointName]>['output']
> = Omit<Parameters<typeof useSchemaQuery<ServiceStandardQueries, EndpointName, E, D>>[0], 'service' | 'serviceName'>;
/**
 * A wrapper around useSchemaQuery that uses the SchedulerV3 service and serviceName.
 * This is intended to only be used internally in the `queries` directory of the library and not exported.
 */
export const useSchedulerV3Query = <
  EndpointName extends StandardQueryEndpointName,
  E = unknown,
  D = SchemaIO<ServiceQueries[EndpointName]>['output']
>({
  options,
  ...args
}: UseSchedulerV3QueryArgs<EndpointName, E, D>) =>
  useSchemaQuery<ServiceStandardQueries, EndpointName, E, D>({
    service: SchemaSchedulerV3Service,
    serviceName,
    ...args,
    options: {
      ...DEFAULT_OPTIONS,
      ...options,
      onSettled: (data, error) => {
        trackScheduleAPIVersionChange('v3', { endpoint: args.endpointName, type: 'single' });
        if (options?.onSettled) {
          options.onSettled(data, error);
        }
      },
    },
  });

export type UseSchedulerV3QueryEndpointArgs<
  EndpointName extends StandardQueryEndpointName,
  E = unknown,
  D = SchemaIO<ServiceStandardQueries[EndpointName]>['output']
> = Omit<UseSchedulerV3QueryArgs<EndpointName, E, D>, 'endpointName'>;
